import type { EPCR, TransportDecision } from '@/types';
import { cn } from '@/lib/cn';
import { Truck, AlertTriangle } from 'lucide-react';
import { TransportMileage } from '@/components/TransportMileage';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoDispositionFields } from '@/lib/demo-data';
import { DispositionDimensionsPanel } from '@/components/DispositionDimensionsPanel';
import { ResponseDelaysPanel } from '@/components/ResponseDelaysPanel';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

const TRANSPORT_DECISIONS: { value: TransportDecision; label: string; description: string }[] = [
  { value: 'transport_this_unit', label: 'Transport by This Unit', description: 'Patient transported to receiving facility' },
  { value: 'transfer_other_unit', label: 'Transfer to Another Unit', description: 'Patient transferred to another EMS unit' },
  { value: 'refusal_care', label: 'Refusal of Care', description: 'Patient refused evaluation and treatment' },
  { value: 'refusal_transport', label: 'Refusal of Transport', description: 'Patient evaluated but refused transport' },
  { value: 'no_patient', label: 'No Patient Found', description: 'No patient on scene at arrival' },
  { value: 'treated_released', label: 'Treated & Released', description: 'Patient treated per protocol and released on scene' },
];

// RI receiving hospitals
const RI_HOSPITALS = [
  'Rhode Island Hospital', 'The Miriam Hospital', 'Hasbro Children\'s Hospital',
  'Women & Infants Hospital', 'Roger Williams Medical Center', 'Our Lady of Fatima Hospital',
  'Kent Hospital', 'Landmark Medical Center', 'South County Hospital',
  'Newport Hospital', 'Westerly Hospital', 'Butler Hospital',
  'Providence VA Medical Center', 'Day Kimball Hospital', 'Sturdy Memorial Hospital',
  'Charlton Memorial Hospital', 'St. Luke\'s Hospital',
];

const HOSPITAL_REASONS = [
  'Closest Facility', 'Patient Request', 'Specialty Center',
  'Diversion Bypass', 'Trauma Center', 'Stroke Center',
  'Cardiac Center', 'Pediatric Center', 'Burn Center',
  'Medical Direction',
];

export function DispositionTab({ epcr, onUpdate }: Props) {
  const isRefusal = epcr.transport_decision === 'refusal_care' || epcr.transport_decision === 'refusal_transport';
  const isTransport = epcr.transport_decision === 'transport_this_unit' || epcr.transport_decision === 'transfer_other_unit';

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <DemoFillButton onFill={() => onUpdate(demoDispositionFields())} />
      </div>

      {/* Four-dimension disposition panel — Foundation §2.16.
          eDisposition.27/.28/.29 captured with distinct rule tags;
          .30 summary read-only (set by the Transport Decision
          big-button bar below). */}
      <DispositionDimensionsPanel epcr={epcr} onUpdate={onUpdate} />

      {/* Response / scene / transport delays — moved here from the
          ePCR top header so the page top stays focused on identity
          and active actions, while delays sit alongside their natural
          context (the disposition of the run). Foundation §2.2. */}
      <ResponseDelaysPanel epcr={epcr} onUpdate={onUpdate} />

      {/* Transport Decision */}
      <section>
        <h3 className="mb-3 text-lg font-semibold text-slate-200">Transport Decision</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {TRANSPORT_DECISIONS.map((td) => (
            <button
              key={td.value}
              type="button"
              onClick={() => onUpdate({ transport_decision: td.value })}
              disabled={epcr.locked}
              className={cn(
                'rounded-lg px-4 py-3 text-left min-h-touch transition-colors',
                epcr.transport_decision === td.value
                  ? 'bg-brand-500 text-white ring-2 ring-brand-400/30'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              )}
            >
              <div className="font-medium">{td.label}</div>
              <div className={cn(
                'text-xs mt-0.5',
                epcr.transport_decision === td.value ? 'text-brand-200' : 'text-slate-500'
              )}>
                {td.description}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Transport destination */}
      {isTransport && (
        <section>
          <h3 className="mb-3 text-lg font-semibold text-slate-200">Destination</h3>
          <SmartDestinationChips
            current={epcr.destination_hospital}
            onApply={(hospital, reason) => onUpdate({
              destination_hospital: hospital,
              hospital_choice_reason: reason,
            })}
            disabled={epcr.locked}
          />
          <div className="grid gap-4 sm:grid-cols-2 mt-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Receiving Hospital</label>
              <select
                value={epcr.destination_hospital ?? ''}
                onChange={(e) => onUpdate({ destination_hospital: e.target.value || null })}
                className="input-field"
                disabled={epcr.locked}
              >
                <option value="">Select hospital...</option>
                {RI_HOSPITALS.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Reason for Choice</label>
              <select
                value={epcr.hospital_choice_reason ?? ''}
                onChange={(e) => onUpdate({ hospital_choice_reason: e.target.value || null })}
                className="input-field"
                disabled={epcr.locked}
              >
                <option value="">Select reason...</option>
                {HOSPITAL_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
        </section>
      )}

      {/* Billing-accurate mileage — addendum § 3 (HCPCS A0425) */}
      {isTransport && (
        <TransportMileage
          epcr={epcr}
          incidentId={epcr.incident_id}
        />
      )}

      {/* Refusal Workflow */}
      {isRefusal && (
        <section className="rounded-lg border border-amber-800 bg-amber-950/30 p-4 space-y-4">
          <div className="flex items-center gap-2 text-amber-400">
            <AlertTriangle size={20} />
            <h3 className="text-lg font-semibold">Refusal Documentation</h3>
          </div>
          <p className="text-sm text-amber-300/80">
            Complete all fields below. Refusal documentation must include capacity assessment,
            risks explained, and patient signature before report can be locked.
          </p>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-amber-300">Capacity Assessment</label>
              <p className="mb-2 text-xs text-slate-400">
                Patient is alert, oriented, and able to understand the nature of the medical emergency
                and the consequences of refusing care/transport.
              </p>
              <div className="flex gap-2">
                <button className="btn-secondary text-sm">Oriented x4</button>
                <button className="btn-secondary text-sm">Oriented x3</button>
                <button className="btn-secondary text-sm">Impaired — Escalate</button>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-amber-300">Risks Explained</label>
              <textarea
                className="input-field min-h-[4rem] resize-y"
                placeholder="Document risks explained to patient: potential deterioration, need for hospital evaluation, inability to diagnose in field..."
                disabled={epcr.locked}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-amber-300">Alternative Offered</label>
              <textarea
                className="input-field min-h-[3rem] resize-y"
                placeholder="Advised patient to call 911 if condition worsens, follow up with PCP within 24-48 hours..."
                disabled={epcr.locked}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-amber-300">Patient Signature</label>
                <div className="flex h-24 items-center justify-center rounded-lg border-2 border-dashed border-amber-800 bg-slate-900">
                  <span className="text-sm text-slate-500">Signature capture — Phase 2B</span>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-amber-300">Witness Signature</label>
                <div className="flex h-24 items-center justify-center rounded-lg border-2 border-dashed border-amber-800 bg-slate-900">
                  <span className="text-sm text-slate-500">Signature capture — Phase 2B</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* No Patient */}
      {epcr.transport_decision === 'no_patient' && (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-4 text-center">
          <Truck size={32} className="mx-auto mb-2 text-slate-600" />
          <p className="text-slate-400">No patient found on scene. Document scene details in narrative.</p>
        </div>
      )}
    </div>
  );
}

/**
 * SmartDestinationChips — one-tap agency routing defaults.
 *
 * Each chip sets BOTH the destination hospital AND the reason in a
 * single tap. Follows the agency's standard routing: RIH for
 * adult trauma/PCI/stroke (co-located adult Level I trauma + PCI +
 * comprehensive stroke), Hasbro for pediatric, Women & Infants for
 * OB. Crew can still pick anything from the dropdown below.
 *
 * Addendum P4 — smart defaults eliminate redundant picks. Six one-tap
 * destinations cover ~85% of Providence transports.
 */
function SmartDestinationChips({
  current, onApply, disabled,
}: {
  current: string | null;
  onApply: (hospital: string, reason: string) => void;
  disabled?: boolean;
}) {
  const chips: Array<{ label: string; hospital: string; reason: string; tone: 'emerald' | 'red' | 'blue' | 'amber' | 'purple' | 'pink' }> = [
    { label: 'Closest (RIH)',   hospital: 'Rhode Island Hospital',     reason: 'Closest Facility',  tone: 'emerald' },
    { label: 'PCI · STEMI',     hospital: 'Rhode Island Hospital',     reason: 'Cardiac Center',    tone: 'red'     },
    { label: 'Stroke',          hospital: 'Rhode Island Hospital',     reason: 'Stroke Center',     tone: 'blue'    },
    { label: 'Trauma Level I',  hospital: 'Rhode Island Hospital',     reason: 'Trauma Center',     tone: 'red'     },
    { label: 'Pediatric',       hospital: "Hasbro Children's Hospital", reason: 'Pediatric Center',  tone: 'amber'   },
    { label: 'OB / Women\'s',   hospital: 'Women & Infants Hospital',  reason: 'Specialty Center',  tone: 'pink'    },
    { label: 'Behavioral',      hospital: 'Butler Hospital',           reason: 'Specialty Center',  tone: 'purple'  },
    { label: 'VA',              hospital: 'Providence VA Medical Center', reason: 'Patient Request', tone: 'blue'    },
  ];
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          1-tap defaults · Providence routing
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {chips.map((c) => {
          const active = current === c.hospital;
          const toneCls = active
            ? (c.tone === 'emerald' ? 'bg-emerald-600 border-emerald-400 text-white' :
               c.tone === 'red'     ? 'bg-red-600 border-red-400 text-white' :
               c.tone === 'blue'    ? 'bg-blue-600 border-blue-400 text-white' :
               c.tone === 'amber'   ? 'bg-amber-600 border-amber-400 text-white' :
               c.tone === 'purple'  ? 'bg-purple-600 border-purple-400 text-white' :
               'bg-pink-600 border-pink-400 text-white')
            : 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700';
          return (
            <button
              key={c.label}
              type="button"
              disabled={disabled}
              onClick={() => onApply(c.hospital, c.reason)}
              className={cn(
                'flex items-baseline gap-1 px-2.5 py-1 text-xs font-bold uppercase tracking-wider border transition-colors',
                toneCls,
              )}
              title={`${c.hospital} · ${c.reason}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
